import { DiagnosisRegistrationStoreData } from '@/pages/demands/contexts/diagnosis-registration-store'
import { confirmDiagnosis } from '@/services/confirm-diagnosis'
import { createDiagnosis } from '@/services/create-diagnosis'

import { DiagnosisMapper } from './diagnosis-mapper'
import { MosaicAdapter } from './mosaic-adapter'
import { PerimeterAdapter } from './perimeter-adapter'

export interface ManagerProps {
  abortController: AbortController
  filesToUpload: DiagnosisRegistrationStoreData['filesToUpload']
  diagnosisCreationData: DiagnosisRegistrationStoreData['diagnosisCreationData']
  registerData: DiagnosisRegistrationStoreData['registerData']
  diagnosisConfirmed: DiagnosisRegistrationStoreData['diagnosisConfirmed']

  onFilesToUpload: (
    filesToUpload: DiagnosisRegistrationStoreData['filesToUpload'],
  ) => void
  onDiagnosisCreated: (
    diagnosisCreationData: DiagnosisRegistrationStoreData['diagnosisCreationData'],
  ) => void
  onDiagnosisConfirmed: () => void

  diagnosisMapper: DiagnosisMapper
  perimeterAdapter: PerimeterAdapter
  mosaicAdapter: MosaicAdapter
  geoTagAdapter: any
  rawImagesAdapter: any
  signedUrlsQueue: any
  uploadQueue: any
}

export class Manager {
  private abortController
  private diagnosisCreationData
  private filesToUpload
  private registerData
  private diagnosisConfirmed

  private onFilesToUpload
  private onDiagnosisCreated
  private onDiagnosisConfirmed

  diagnosisMapper
  perimeterAdapter
  mosaicAdapter
  geoTagAdapter
  rawImagesAdapter
  signedUrlsQueue
  uploadQueue

  constructor(props: ManagerProps) {
    this.abortController = props.abortController
    this.diagnosisCreationData = props.diagnosisCreationData
    this.filesToUpload = props.filesToUpload
    this.registerData = props.registerData
    this.diagnosisConfirmed = props.diagnosisConfirmed

    this.onFilesToUpload = props.onFilesToUpload
    this.onDiagnosisCreated = props.onDiagnosisCreated
    this.onDiagnosisConfirmed = props.onDiagnosisConfirmed

    this.diagnosisMapper = props.diagnosisMapper
    this.perimeterAdapter = props.perimeterAdapter
    this.mosaicAdapter = props.mosaicAdapter
    this.geoTagAdapter = props.geoTagAdapter
    this.rawImagesAdapter = props.rawImagesAdapter
    this.signedUrlsQueue = props.signedUrlsQueue
    this.uploadQueue = props.uploadQueue
  }

  async execute() {
    const { demand_id, mosaic_id } = await this.handleCreateDiagnosis()

    if (!this.filesToUpload) {
      await this.enqueuePerimeters(demand_id)
      this.enqueueMosaic(mosaic_id)
      this.enqueueGeoTag(demand_id)
      this.enqueueRawImages()
      this.filesToUpload = await this.signedUrlsQueue.generateSignedUrls(
        demand_id,
      )
      this.onFilesToUpload(this.filesToUpload!)
    }
    await this.uploadQueue.execute(demand_id, this.filesToUpload)
    await this.handleConfirmDiagnosis(demand_id)
  }

  async handleCreateDiagnosis() {
    if (this.diagnosisCreationData) return this.diagnosisCreationData

    this.diagnosisCreationData = await createDiagnosis(
      this.registerData!.treatment,
      this.registerData!.culture,
      this.diagnosisMapper.build(),
      { signal: this.abortController.signal },
    )

    this.onDiagnosisCreated(this.diagnosisCreationData)

    return this.diagnosisCreationData
  }

  async enqueuePerimeters(diagnosisId: string) {
    this.signedUrlsQueue.enqueue(
      await this.perimeterAdapter.makeFileReference(diagnosisId),
    )
  }

  enqueueMosaic(mosaicId?: string) {
    const mosaic = this.mosaicAdapter.makeFileReference(mosaicId)
    if (!mosaic) return

    this.signedUrlsQueue.enqueue(mosaic)
  }

  enqueueGeoTag(diagnosisId: string) {
    const geotag = this.geoTagAdapter.makeFileReference(diagnosisId)
    if (!geotag) return

    this.signedUrlsQueue.enqueue(geotag)
  }

  enqueueRawImages() {
    const rgbFileReference = this.rawImagesAdapter.makeRgbFileReference()
    if (rgbFileReference) {
      this.signedUrlsQueue.enqueueBatchFiles(rgbFileReference)
    }

    const thermalFileReference =
      this.rawImagesAdapter.makeThermalFileReference()
    if (thermalFileReference) {
      this.signedUrlsQueue.enqueueBatchFiles(thermalFileReference)
    }
  }

  async handleConfirmDiagnosis(diagnosisId: string) {
    if (this.diagnosisConfirmed) return

    await confirmDiagnosis(
      this.registerData!.treatment,
      this.registerData!.culture,
      diagnosisId,
      { signal: this.abortController.signal },
    )

    this.diagnosisConfirmed = true
    this.onDiagnosisConfirmed()
  }
}
